import {
  brandclub_colors,
  CheckMarkIcon,
  getEntityImage,
  MOBILE_SCREEN_SIZE,
  TABLET_SCREEN_SIZE,
} from "@brandclub/common-ui";
import { Box, styled, useMediaQuery, useTheme } from "@mui/material";
import { StoreAnimatedHeightDiv } from "../../../../StoreComponents/StoreAnimatedHeightDiv";

import { useCheckoutContext } from "@/components/Checkout/CheckoutMachine";
import { CheckoutState } from "@/components/Checkout/CheckoutMachine/types/CheckoutMachine";

import { useSelector } from "@xstate/react";
import { startCase } from "lodash";
import Loading from "@/components/Loading";
import { StoreErrorV2 } from "@/components/StoreComponents/StoreError";
import { AsyncCheckoutOrderType } from "@/components/Checkout/types";
import { useRetailerName } from "@/utils/hooks/useRetailerName";

const PillContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "8px 17px",
  fontSize: "14px",
  fontWeight: 600,
  borderRadius: 999,
  backgroundColor: brandclub_colors.blueWhite,
  border: `1px solid ${brandclub_colors.lightGray}`,
  [theme.breakpoints.down("sm")]: {
    padding: "4px 8px",
    fontSize: "12px",
    gap: "5px",
  },
  ".pill-icon": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "20px",
    height: "20px",
    [theme.breakpoints.down("sm")]: {
      width: "16px",
      height: "16px",
    },
  },
}));

const StatePill = ({
  state,
}: {
  state: "pending" | "connected" | "disconnected";
}) => {
  const displayText = state === "pending" ? "Connecting" : startCase(state);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  if (state === "disconnected") {
    return (
      <StoreErrorV2
        errorMessage={displayText}
        sx={{
          margin: "0 0",
        }}
      />
    );
  }
  return (
    <PillContainer className="state-pill">
      <div className="pill-icon">
        {state === "pending" ? (
          <Loading
            storeSpin
            size={isMobile ? 16 : 20}
            spinCustomColor={theme.vars.palette.text.primary}
          />
        ) : null}
        {state === "connected" ? (
          <CheckMarkIcon
            width={isMobile ? 16 : 20}
            height={isMobile ? 16 : 20}
            sx={(theme) => ({
              width: 20,
              height: 20,
              [theme.breakpoints.down("sm")]: {
                width: 16,
                height: 16,
              },
            })}
          />
        ) : null}
      </div>
      {displayText}
    </PillContainer>
  );
};

const AccountPreviewContainer = styled(Box)(({ theme }) => ({
  display: "flex",

  flexDirection: "column",
  fontSize: "16px",
  gap: "10px",
  ".message": {
    marginBottom: "10px",
  },
  ".account-info": {
    display: "flex",
    gap: "10px",
    alignItems: "center",
    width: "fit-content",
    ".title": {
      whiteSpace: "nowrap",
    },
  },
  color: theme.vars.palette.text.primary,
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {},
  [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
    fontSize: "14px",
    gap: "7px",
  },
  ".logo": {
    width: "25px",
    height: "25px",
    [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {},
    [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      width: "20px",
      height: "20px",
    },
  },
}));

const AccountPreview = () => {
  const { actorRef } = useCheckoutContext();

  const retailerUsername = useSelector(
    actorRef,
    (state) =>
      state.context.retailerCheckoutOrderStatus?.userRetailerCheckoutOrder
        ?.userCheckoutInfo?.retailerConnection?.username
  );

  const orderType = useSelector(
    actorRef,
    (state) =>
      state.context.retailerCheckoutOrderStatus?.userRetailerCheckoutOrder
        ?.orderType
  );

  const isGuest = orderType === AsyncCheckoutOrderType.GUEST;

  const headerDisplayConfig = useSelector(
    actorRef,
    (state) => state.context.retailerCheckoutOrderStatus?.headerDisplayConfig
  );

  const infoMessage = headerDisplayConfig?.accountConfig?.displayMessage;

  const retailerId = useSelector(
    actorRef,
    (state) =>
      state.context.retailerCheckoutOrderStatus?.userRetailerCheckoutOrder
        ?.retailerId
  );

  const retailerName = useRetailerName(retailerId ?? 0);

  const retailerAccount = retailerUsername ?? "Unknown";

  const connectStatus = headerDisplayConfig?.accountConfig?.status;
  return (
    <AccountPreviewContainer>
      {infoMessage ? <div className="message">{infoMessage}</div> : null}
      <div className="account-info">
        {retailerId ? (
          <img
            alt="logo"
            className="logo"
            src={getEntityImage(retailerId, "retailer")}
          />
        ) : null}
        {!isGuest ? (
          <>
            <div className="title">{retailerAccount}</div>
            {connectStatus ? <StatePill state={connectStatus} /> : null}
          </>
        ) : (
          <div className="title">{retailerName}</div>
        )}
      </div>
    </AccountPreviewContainer>
  );
};

const AccountSectionV2 = () => {
  const { actorRef } = useCheckoutContext();

  const userCheckoutInfo = useSelector(
    actorRef,
    (state) =>
      state.context.retailerCheckoutOrderStatus?.userRetailerCheckoutOrder
        ?.userCheckoutInfo
  );

  const status = useSelector(
    actorRef,
    (snapshot) => snapshot.context.checkoutState
  );

  // if there is no username, skip the account section for receipt generated state
  if (
    status === CheckoutState.RECEIPT_GENERATED &&
    !userCheckoutInfo?.retailerConnection?.username
  ) {
    return null;
  }

  return (
    <Box className="section">
      <div className="section_head" style={{ cursor: "pointer" }}>
        <div className="title">Checkout method</div>
      </div>
      <StoreAnimatedHeightDiv>
        <AccountPreview />
      </StoreAnimatedHeightDiv>
    </Box>
  );
};

export default AccountSectionV2;
